import { useRenameInternalOpsGroupChannel } from "@/lib/api/internal-ops-messaging";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { PencilIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

interface RenameableGroupNameProps {
  channelId: string;
  displayName: string;
}

export function RenameableGroupName({
  channelId,
  displayName,
}: RenameableGroupNameProps) {
  const { toast } = useToast();
  const renameChannel = useRenameInternalOpsGroupChannel();
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(displayName);

  const handleStartEditing = () => {
    setDraftName(displayName);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraftName(displayName);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedName = draftName.trim();
    if (!trimmedName) {
      toast({
        title: "Group name required",
        description: "Enter a name for this group.",
        variant: "destructive",
      });
      return;
    }

    if (trimmedName === displayName.trim()) {
      setIsEditing(false);
      return;
    }

    try {
      await renameChannel.mutateAsync({
        channelId,
        displayName: trimmedName,
      });
      setIsEditing(false);
    } catch {
      toast({
        title: "Could not rename group",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isEditing) {
    return (
      <div className="flex flex-col gap-2">
        <Input
          value={draftName}
          onChange={(event) => setDraftName(event.target.value)}
          maxLength={120}
          autoFocus
          aria-label="Group name"
          onKeyDown={(event) => {
            if (event.key === "Enter" && !renameChannel.isPending) {
              event.preventDefault();
              void handleSave();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              handleCancel();
            }
          }}
        />
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={renameChannel.isPending}
          >
            Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={renameChannel.isPending}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <h2 className="min-w-0 flex-1 text-xl font-bold leading-7 text-[#020817]">
        {displayName}
      </h2>
      <button
        type="button"
        onClick={handleStartEditing}
        className="mt-1 shrink-0 text-zinc-400 hover:text-zinc-600"
        aria-label="Rename group"
      >
        <PencilIcon className="size-4" />
      </button>
    </div>
  );
}
